import React from 'react';
import { formatBytes } from '@/utils';

interface FilePreviewCardProps {
  fileName: string;
  fileSize?: number;
  statusLabel?: string;
  showStatus?: boolean;
  onRemove?: () => void;
  /** Replaces the default PDF icon — e.g. an image thumbnail. */
  leading?: React.ReactNode;
  /** When set, renders a "Replace" action beside the remove button. */
  onReplace?: () => void;
  className?: string;
}

const PdfIcon = () => <i className="ri-file-pdf-2-line text-2xl text-gray-600" />;

const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-green-500"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-500 hover:text-red-500"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const FilePreviewCard: React.FC<FilePreviewCardProps> = ({
  fileName,
  fileSize,
  statusLabel = 'Completed',
  showStatus = true,
  onRemove,
  leading,
  onReplace,
  className = '',
}) => (
  <div
    className={`p-4 flex items-center justify-between border border-gray-200 rounded-lg ${className}`.trim()}
  >
    <div className="flex items-center space-x-3 min-w-0">
      {leading ?? <PdfIcon />}
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-800 truncate">
          {fileName}
        </p>
        {(fileSize !== undefined || showStatus) && (
          <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
            {fileSize !== undefined && <span>{formatBytes(fileSize)}</span>}
            {showStatus && (
              <>
                <CheckCircleIcon />
                <span className="text-green-600 font-medium">
                  {statusLabel}
                </span>
              </>
            )}
          </div>
        )}
      </div>
    </div>
    <div className="flex items-center space-x-3 flex-shrink-0">
      {onReplace && (
        <button
          type="button"
          onClick={onReplace}
          className="text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          Replace
        </button>
      )}
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove file">
          <TrashIcon />
        </button>
      )}
    </div>
  </div>
);

export default FilePreviewCard;
